import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { InventoryActionRawData, InventoryActionParsedData } from "../../../interfaces/reports/InventoryActionReport.interface";
import { MoneyDataItem, PercentDataItem, StringDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";

const TODAY_EPOCH_MONTH = EpochMonthService.getTodayEpochMonth();

interface InventoryActionReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
}

const normalizeCategorySortOrder = (value: unknown): number | null => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

const normalizeCategory = (value: unknown): string => {
    return typeof value === "string" && value.trim() !== "" ? value : "N/A";
}

const buildCategorySortOrderMap = (rawData: InventoryActionRawData[]): Record<string, number> => {
    const orderMap: Record<string, number> = {};

    for (const item of rawData) {
        const category = normalizeCategory(item.category);
        const order = normalizeCategorySortOrder(item.categorySortOrder);
        if (order == null) continue;

        if (!(category in orderMap) || order < orderMap[category]) {
            orderMap[category] = order;
        }
    }

    return orderMap;
}
 
// Moved data processing to a separate function
const processRawData = (
    rawData: InventoryActionRawData[],
    trend: number,
    isCost: boolean = false,
    categorySortOrderMap: Record<string, number> = {}
): InventoryActionParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    // Parse the raw data into controlled and formattable types
    return rawData.map((item) => {
        const normalizedCategory = normalizeCategory(item.category);
        const rawCategorySortOrder = normalizeCategorySortOrder(item.categorySortOrder);
        const hydratedCategorySortOrder = rawCategorySortOrder ?? categorySortOrderMap[normalizedCategory] ?? null;
        // Keep N/A (empty/null) category at the end in all cases.
        const categorySortOrder = normalizedCategory === "N/A"
            ? Number.MAX_SAFE_INTEGER
            : hydratedCategorySortOrder;

        const markdownPercent = (item.sales && item.markdowns) ? (item.markdowns / item.sales) : 0;
        const costMultiplier = isCost ? (item.costComplement ?? 1) : 1;

        const sales = item.sales;
        const salesTrend = sales / (trend || 1);
        const inflowTrend = Number(ReportService.calculateInflowTrend({
            invBomNM: item.inventoryPlan,
            currInv: item.inventory,
            sales: item.sales,
            salesPlan: item.salesPlan,
            mdRemaining: item.markdownsRemaining,
            trend: trend,
            format: false,
        }));
        const onOrderThisMonth = isCost ? item.onOrderCostThisMonth : item.onOrderThisMonth;
        const otbTrend = item.otbUpdated - item.otbUpdatedThisMonth + inflowTrend
        const invTrend = item.inventory - (salesTrend - sales) - item.markdownsRemaining + item.onOrderThisMonth;

        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            parentClientClassCode: item.parentClientClassCode ?? null,
            category: normalizedCategory,
            categorySortOrder,

            sales: new MoneyDataItem(item.sales),
            salesPlan: new MoneyDataItem(item.salesPlan),
            salesTrend: new MoneyDataItem(salesTrend),
            markdowns: new MoneyDataItem(item.markdowns),
            markdownsRemaining: new MoneyDataItem(item.markdownsRemaining),
            markdownsPercent: new PercentDataItem(markdownPercent * 100),
            inventory: new MoneyDataItem(item.inventory ),
            inventoryPlan: new MoneyDataItem(item.inventoryPlan ),
            inventoryTrend: new MoneyDataItem(invTrend),
            inflowActual: new MoneyDataItem(item.inflowRetail),
            inflowUpdated: new MoneyDataItem(item.inflowUpdated),
            updatedInflowPercent: new PercentDataItem(0),
            inflowTrend: new MoneyDataItem(inflowTrend),
            inflowTrendPercent: new PercentDataItem(0),
            onOrderThisMonth: new MoneyDataItem(onOrderThisMonth),
            onOrder: new MoneyDataItem(isCost ? item.onOrderCost : item.onOrder),
            otbUpdated: new MoneyDataItem(isCost ? item.otbUpdatedCost : item.otbUpdated),
            otbTrend: new MoneyDataItem((otbTrend * (costMultiplier || 1)) - onOrderThisMonth),

            openToBuy: (item.openToBuy ?? 0) * costMultiplier,
            costComplement: item.costComplement ?? 0,
            trend: item.trend ?? 0,

            nonNegativeInflowTrend: new MoneyDataItem(inflowTrend && inflowTrend > 0 ? inflowTrend : 0),
            nonNegativeInflowUpdated: new MoneyDataItem(item.inflowUpdated && item.inflowUpdated > 0 ? item.inflowUpdated : 0),
        }
    });
};

const reapplyPercentCalcs = (data: InventoryActionParsedData[]) => {
    // Reapply percent calculations based on the hierarchy groups
    return data.map(item => {
        // The percent calculations will be handled by ReportService grouping
        // Just recalculate the derived metrics
        const markdownPercent =
            item.sales && item.sales.value && item.markdowns?.value
                ? (Number(item.markdowns.value) || 0) / (Number(item.sales.value) || 1)
                : 0;

        return {
            ...item,
            markdownsPercent: new PercentDataItem(markdownPercent * 100),
        };
    });
}

const InventoryActionReport = ({ clientId, epochMonth }: InventoryActionReportProps) => {
    const globalTip = TipContext.useMessage();
    const dataRef = useRef<InventoryActionRawData[]>([]);
    const parentDataRef = useRef<InventoryActionRawData[]>([]);
    const [parsedData, setParsedData] = useState<InventoryActionParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<InventoryActionParsedData[]>([]);
    const [sortedData, setSortedData] = useState<InventoryActionParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["locationCode", "classCode"]);
    const [customTrend, setCustomTrend] = useState<number>(1);
    const [pricingType, setPricingType] = useState<"Retail" | "Cost">("Retail");
    const [monthsForward, setMonthsForward] = useState<number>(3);

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const columns = [
        ...hierarchyColumns,
        { name: "Sales", sortable: true },
        { name: "Sales Plan", sortable: true },
        { name: "Sales Trend", sortable: true },
        { name: "MD", sortable: true },
        { name: "MD %", sortable: true },
        { name: "Inv", sortable: true },
        { name: "Inv Plan", sortable: true },
        { name: "Inv Trend", sortable: true },
        { name: "Inf. Actual", sortable: true },
        { name: "Inf. Updated", sortable: true },
        { name: "Updated Inflow %", sortable: true },
        { name: "Inf. Trend", sortable: true },
        { name: "Inf. Trend %", sortable: true },
        { name: "On Order", sortable: true },
        { name: "OTB Updated", sortable: true },
        { name: "OTB Trend", sortable: true },

    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            console.log(monthsForward);
            ReportService.getInventoryActionReportData({ clientId, epochMonth, monthsForward }).then((response) => {
                if (response) {
                    // Store raw data in refs
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];
                    const categorySortOrderMap = buildCategorySortOrderMap(dataRef.current);

                    // find max trend value
                    let maxTrend = 0;
                    (response.data ?? []).forEach((item: InventoryActionRawData) => {
                        if (item.trend && item.trend > maxTrend) {
                            maxTrend = item.trend;
                        }
                    });
                    setCustomTrend(maxTrend);

                    // Process both child and parent data
                    const processed = processRawData(response.data ?? [], maxTrend, pricingType === "Cost", categorySortOrderMap);
                    const processedParent = processRawData(response.parentLevelData ?? [], maxTrend, pricingType === "Cost", categorySortOrderMap);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    // Default: use child data
                    let sorted = ReportService.groupByHierarchy(processed, hierarchy ?? ["locationCode", "classCode"]);
                    sorted = reapplyPercentCalcs(sorted);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch((error) => {
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth, monthsForward]);

    useEffect(() => {
        // Reprocess data when customTrend or pricingType changes
        const categorySortOrderMap = buildCategorySortOrderMap(dataRef.current);
        let newParsedData = processRawData(dataRef.current, customTrend, pricingType === "Cost", categorySortOrderMap);
        setParsedData(newParsedData);
        let newParentParsedData = processRawData(parentDataRef.current, customTrend, pricingType === "Cost", categorySortOrderMap);
        setParentParsedData(newParentParsedData);
    }, [customTrend, pricingType]);

    useEffect(() => {
        // Use parent or child data based on hierarchy
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyPercentCalcs(newSortedData);
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter, pricingType]);

    const tableData = useMemo(() => {
        if (!sortedData.length) return [];
        // Get first row's inflowUpdated and inflowTrend values (raw, not formatted)
        const firstInflowUpdated = Number(sortedData[0]?.nonNegativeInflowUpdated?.value || 1);
        
        // get sum of nonNegativeInflowTrend values for all rows
        const firstInflowTrend = sortedData.reduce((sum, item) => {
            const value = Number(item.nonNegativeInflowTrend?.value || 0);
            return sum + (value > 0 ? value : 0);
        }, 0);
    
        return sortedData.map((item, index) => {
            const inflowUpdatedValue = Number(item.inflowUpdated?.value || 0);
            const inflowTrendValue = Number(item.inflowTrend?.value || 0);
            // Calculate updatedInflowPercent and inflowTrendPercent as percent of first row
            const updatedInflowPercent = inflowUpdatedValue && firstInflowUpdated && inflowUpdatedValue > 0
                ? (inflowUpdatedValue / firstInflowUpdated) * 100
                : 0;
            const inflowTrendPercent = firstInflowTrend && inflowTrendValue && inflowTrendValue > 0
                ? (inflowTrendValue / firstInflowTrend) * 100
                : 0;
    
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);
    
            return [
                ...labelRows,
                item.sales?.formatted,
                item.salesPlan?.formatted,
                item.salesTrend?.formatted,
                item.markdowns?.formatted,
                item.markdownsPercent?.formatted,
                item.inventory?.formatted,
                item.inventoryPlan?.formatted,
                item.inventoryTrend?.formatted,
                item.inflowActual?.formatted,
                item.inflowUpdated?.formatted,
                `${updatedInflowPercent.toFixed(0)}%`,
                item.inflowTrend?.formatted,
                `${inflowTrendPercent.toFixed(0)}%`,
                item.onOrder?.formatted,
                item.otbUpdated?.formatted,
                item.otbTrend?.formatted,
            ];
        });
    }, [sortedData, hierarchy, customTrend, pricingType]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    const nonNullEpochMonth = epochMonth ?? TODAY_EPOCH_MONTH;

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`Inventory Action Report - ${pricingType} - ${EpochMonthService.epochMonthToMMMYY(nonNullEpochMonth)} to ${EpochMonthService.epochMonthToMMMYY(nonNullEpochMonth + monthsForward)}`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="h-full max-w-[1700px]">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title="Inventory Action Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Inventory_Action_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}
                        customTrend={customTrend}
                        setCustomTrend={setCustomTrend}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            // Reparse
                            const categorySortOrderMap = buildCategorySortOrderMap(dataRef.current);
                            let parsedData = processRawData(dataRef.current, customTrend, pricingType === "Cost", categorySortOrderMap);
                            setParsedData(parsedData);
                            let parentParsedData = processRawData(parentDataRef.current, customTrend, pricingType === "Cost", categorySortOrderMap);
                            setParentParsedData(parentParsedData);
                        }}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Pricing Type</span>
                            <select
                                value={pricingType}
                                onChange={e => {
                                    const val = e.target.value;
                                    setPricingType(val === 'Retail' ? 'Retail' : 'Cost');
                                    // Reparse
                                    const categorySortOrderMap = buildCategorySortOrderMap(dataRef.current);
                                    let parsedData = processRawData(dataRef.current, customTrend, val === "Cost", categorySortOrderMap);
                                    setParsedData(parsedData);
                                    let parentParsedData = processRawData(parentDataRef.current, customTrend, val === "Cost", categorySortOrderMap);
                                    setParentParsedData(parentParsedData);
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="Retail">Retail</option>
                                <option value="Cost">Cost</option>
                            </select>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Months Forward for Trend</span>
                            <select
                                value={monthsForward}
                                onChange={e => {
                                    const val = parseInt(e.target.value);
                                    setMonthsForward(val);
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value={0}>Current Month Only</option>
                                <option value={1}>1 Month</option>
                                <option value={2}>2 Months</option>
                                <option value={3}>3 Months</option>
                                <option value={4}>4 Months</option>
                                <option value={5}>5 Months</option>
                                <option value={6}>6 Months</option>
                                <option value={7}>7 Months</option>
                                <option value={8}>8 Months</option>
                                <option value={9}>9 Months</option>
                                <option value={10}>10 Months</option>
                                <option value={11}>11 Months</option>
                            </select>
                        </div>
                    </div>
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface InventoryActionReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const InventoryActionReportModal = ({ clientId, epochMonth, onClose }: InventoryActionReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <InventoryActionReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    InventoryActionReport,
    InventoryActionReportModal
}